/** Fixed variants keep public image storage and conversion work bounded. */
export const IMAGE_WIDTHS = [
  64,
  128,
  256,
  320,
  384,
  480,
  512,
  640,
  672,
  768,
  960,
  1280,
  1600,
  1920,
] as const;
export type ImageWidth = (typeof IMAGE_WIDTHS)[number];

export const CARD_IMAGE_WIDTHS = [256, 320, 384] as const;
export const THUMBNAIL_IMAGE_WIDTHS = [64, 128, 256] as const;
export const CONTENT_PREVIEW_IMAGE_WIDTHS = [64, 128, 256, 320, 384] as const;
export const MOBILE_HERO_IMAGE_WIDTHS = [320, 384, 480, 512, 640, 672, 768, 960] as const;
export const DESKTOP_HERO_IMAGE_WIDTHS = [768, 960, 1280, 1600, 1920] as const;
export const HERO_IMAGE_WIDTHS = [320, 384, 480, 512, 640, 672, 768, 960, 1280, 1600, 1920] as const;
export const WEBSITE_PRODUCT_CARD_SIZES =
  '(min-width: 1280px) 272px, (min-width: 1024px) 360px, (min-width: 640px) calc((100vw - 64px) / 2), calc(100vw - 40px)';

const STATIC_IMAGE_VERSIONS: Record<string, string> = {
  '/assets/branding/banner_mobile.webp': 'ced6354862ea',
  '/assets/branding/banner_desktop.webp': 'ca8eaea58050',
};

export function staticImageVersion(src: string): string | undefined {
  return STATIC_IMAGE_VERSIONS[src];
}

export function responsiveImage(
  src: string,
  widths: readonly ImageWidth[],
  sizes: string,
): { src: string; srcSet?: string; sizes?: string } {
  // Only optimize our public raster uploads. External URLs, SVGs and private files stay untouched.
  const match =
    /^(\/[^?#]*)?\/assets\/(products|categories|manufacturers|website|branding)\/(.+\.(?:png|jpe?g|webp|avif))$/i.exec(
      src,
    );
  if (!match || src.startsWith('//') || src.includes('..') || src.includes('\\') || !widths.length)
    return { src };
  const prefix = match[1] ?? '';
  const unprefixedSource = src.slice(prefix.length);
  const version = staticImageVersion(unprefixedSource);
  const url = (width: ImageWidth) => {
    const params = new URLSearchParams({ src, w: String(width) });
    if (version) params.set('v', version);
    return `${prefix}/web/image?${params.toString()}`;
  };
  return {
    src: url(widths[0]!),
    srcSet: widths.map((width) => `${url(width)} ${width}w`).join(', '),
    sizes,
  };
}
